import { TEntryForm, TRequirementFieldConfig } from '@/components/DynamicForm/model';
import { DndContext, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core/dist/types';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Form, Typography } from 'antd';
import classNames from 'classnames';
import { isEmpty, keyBy, noop } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import './BaseRequirementForm.less';
import {
  fieldNameMap,
  getFieldKey,
  multipleValueTypes,
  shouldChainOnAfterChange,
  systemTypeKeyMap,
  wrapFieldValue,
} from './utils';
import { getCustomFieldWidget, getSystemFieldWidget } from './utils/component';

const SortableItem = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: props.isDragging ? '100' : 'auto',
    opacity: props.isDragging ? 0.3 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {props.children}
    </div>
  );
};

// 添加类型定义
interface CustomField {
  id: string;
  name: string;
  widgetType: string;
  colorEnabled?: boolean;
  choices?: any[];
  fieldOption?: any;
  deleted?: boolean;
}

export default function BaseRequirementForm({
  requirement,
  customFieldList,
  fields,
  disabledFields,
  submittingField,
  secretKey,
  sortable,
  selectedItem,
  onFieldSelect,
  onAfterChange,
  setFieldsValueChanged,
  errorFields = [],
  isIgnoreField,
  onSortEnd,
}: {
  requirement?: TEntryForm;
  customFieldList: CustomField[];
  fields: TRequirementFieldConfig[];
  disabledFields?: string[];
  submittingField?: any;
  secretKey?: string;
  sortable?: boolean;
  selectedItem?: any;
  onFieldSelect?: (fieldKey: string) => void;
  setFieldsValue?: (fields: any, props: any) => void;
  onAfterChange?: (fieldKey: string, value: any) => void;
  setFieldsValueChanged?: (fieldKey: string, value: any) => void;
  errorFields?: any[];
  isIgnoreField?: (field: TRequirementFieldConfig) => boolean;
  onSortEnd?(event: DragEndEvent): void;
}) {
  const [customFieldMap, setCustomFieldMap] = useState<Record<string, CustomField>>({});
  const attachmentRef = useRef();
  const customFields = requirement?.customFields || [];
  const errorFieldsKey = errorFields?.map((item) => item.fieldKey || '') || [];
  const [activeId, setActiveId] = useState<string>();

  const isTicket = true;

  useEffect(() => {
    setCustomFieldMap(
      keyBy(
        (customFieldList || []).filter(({ deleted }) => !deleted),
        'id',
      ),
    );
  }, [customFieldList]);

  useEffect(() => {
    if (isEmpty(errorFields)) {
      return;
    }

    const items = document.getElementsByClassName('requirement-field-item ant-form-item-has-error');
    items?.[0]?.scrollIntoView({ behavior: 'smooth' });
  }, [errorFieldsKey]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const renderField = (field: TRequirementFieldConfig) => {
    const { fieldType, customFieldEntityId, description, required, fieldKey } = field;
    let Component = null;
    let onChange = noop;

    let label = null;
    let value = undefined;
    const customField =
      fieldType === 'custom' && customFieldEntityId ? customFieldMap[customFieldEntityId] : null;
    const formKey = fieldType === 'custom' ? `custom_${customField?.id || ''}` : fieldType || '';
    const handleAfterChange = (options: any) => {
      if (!onAfterChange) {
        return;
      }
      const { widgetType } = customField || {};
      onAfterChange(formKey, {
        doChain: shouldChainOnAfterChange({ fieldType: fieldType || '', widgetType }),
        ...options,
      });
    };
    const handleSetFieldsValue = (value: any, _props: any) => {
      // 使用 _props 表示有意忽略这个参数
    };
    const validateStatus =
      submittingField === formKey ? 'validating' : errorFieldsKey.includes(fieldKey) ? 'error' : '';
    let multiple = false;
    if (fieldType && fieldType !== 'custom') {
      label = fieldNameMap[fieldType];
      const originFieldType = systemTypeKeyMap[fieldType] || fieldType;
      Component = getSystemFieldWidget(fieldType);
      onChange = (value, props) => {
        handleSetFieldsValue({ [originFieldType]: value }, props);
      };

      value = requirement ? (requirement as any)[originFieldType] : undefined;
    } else {
      if (!customField) {
        return null;
      }

      const { widgetType, id, name } = customField;
      if (widgetType) {
        Component = getCustomFieldWidget(widgetType);
        label = name;
        value = customFields.find(
          (item) => (item as any).customFieldId === id || item.id === id,
        )?.value;
        value = wrapFieldValue(value, widgetType);
        if (multipleValueTypes.includes(widgetType)) {
          multiple = true;
        }
      }
    }

    // console.log(`Component: ${typeof Component}`)
    if (!Component) {
      return null;
    }

    const isDisabled = (fieldType: string, customField: CustomField | null) => {
      // 工单模式禁用成员控件
      if (customField) {
        if (isTicket && customField.widgetType === 'member') {
          return true;
        }
        return false;
      }
      if (
        isTicket &&
        ['handler', 'dataset', 'reportList', 'report', 'indexList'].includes(fieldType)
      ) {
        return true;
      }
      return disabledFields && disabledFields.includes(fieldType);
    };

    const rules = required ? [{ required: true, message: '不能为空' }] : [];
    const ignore = isIgnoreField ? isIgnoreField(field) : false;

    return ignore ? (
      <></>
    ) : (
      <Form.Item
        label={label ? `${label}：` : ''}
        className="requirement-field-item"
        layout="vertical"
        required={required}
        validateStatus={validateStatus}
        hasFeedback={validateStatus === 'validating'}
        hidden={isIgnoreField ? isIgnoreField(field) : false}
      >
        <Typography.Paragraph className="requirement-field-item-tips">
          {description}
        </Typography.Paragraph>
        <Form.Item name={field.id} noStyle rules={rules}>
          <Component
            onChange={onChange}
            typeField={field}
            customField={customField}
            colorEnabled={customField?.colorEnabled}
            choices={customField?.choices}
            value={value}
            label={label}
            secretKey={secretKey}
            requirement={requirement}
            setFieldsValue={handleSetFieldsValue}
            onAfterChange={handleAfterChange}
            setFieldsValueChanged={setFieldsValueChanged}
            placeholder={field?.placeholder}
            disabled={isDisabled(fieldType, customField)}
            fieldOption={{ ...customField?.fieldOption, format: customField?.widgetType }}
            attachmentRef={attachmentRef}
            multiple={multiple}
          />
        </Form.Item>
      </Form.Item>
    );
  };

  const renderFieldWrap = (field) => {
    let item = renderField(field);
    if (item) {
      const itemProps = {
        className: classNames(item.props?.className, { selected: selectedItem === field.id }),
        onMouseDown: () => {
          if (onFieldSelect) {
            onFieldSelect(field.id);
          }
        },
      };
      if (!sortable) {
        itemProps.key = getFieldKey(field);
      }
      item = React.cloneElement(item, itemProps);
    }
    if (sortable && item) {
      return (
        <SortableItem id={field.id} key={field.id}>
          {item}
        </SortableItem>
      );
    }
    return item;
  };

  const draggingActiveItem = fields.find((item) => item.id === activeId);

  return (
    <div className={classNames('base-requirement-form', { mobile: isMobile })}>
      {sortable ? (
        <DndContext onDragStart={handleDragStart} onDragEnd={onSortEnd}>
          <SortableContext items={fields} strategy={verticalListSortingStrategy}>
            {fields.map(renderFieldWrap)}
          </SortableContext>
          <DragOverlay style={{ transformOrigin: '0 0 ' }}>
            {activeId && draggingActiveItem ? (
              <SortableItem id={activeId} key={activeId} isDragging>
                {renderFieldWrap(draggingActiveItem)}
              </SortableItem>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <>{fields.map(renderFieldWrap)}</>
      )}
    </div>
  );
}
